import { CustomCheckbox, Flex } from 'portal-commons';
import React, {
  FunctionComponent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableCell, TableRow } from '@material-ui/core';

import MnoStatusSection from './MnoStatusSection';
import { CspCampaign } from '../../types';
import { remapTCRStatus } from '../../utils';
import { MnoEnum } from '@/apis/core';
import { convertTimeWithTimezone } from '@/utils/time';

interface MyCampaignListingRowProps {
  selectable?: boolean;
  selected?: boolean;
  error?: boolean;
  campaign: CspCampaign;
  mnos: MnoEnum[];
  onChange?: (selected: boolean) => void;
}

const MyCampaignListingRow: FunctionComponent<MyCampaignListingRowProps> = ({
  selectable = false,
  selected = false,
  error = false,
  campaign,
  mnos,
  onChange,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [additionalHeight, setAdditionalHeight] = useState(0);
  const additionalCellStyle = useMemo(
    () =>
      expanded
        ? { paddingTop: '12px' }
        : { height: '38px', paddingTop: 0, paddingBottom: 0 },
    [expanded]
  );
  const mnoStatusSectionRef = useRef<HTMLDivElement>(null);

  const handleExpand = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (mnoStatusSectionRef.current) {
      setAdditionalHeight(mnoStatusSectionRef.current.clientHeight);
    } else {
      setAdditionalHeight(0);
    }
  }, [mnoStatusSectionRef.current]);

  return (
    <TableRow
      className="link"
      style={{
        position: 'relative',
        height: expanded ? `calc(38px + ${additionalHeight}px)` : '38px',
        overflow: 'hidden',
        transition: expanded ? 'height 0.3s ease-in-out' : 'none',
        ...(expanded ? { verticalAlign: 'top' } : {}),
        ...(error
          ? {
              background: '#F5DDE2',
            }
          : {}),
      }}
      onClick={() =>
        history.push({
          pathname: `/campaigns/${campaign.uid}`,
          state: {
            goBackPage: `${location.pathname}${
              location.search ? location.search : ''
            }`,
          },
        })
      }
    >
      {selectable && (
        <TableCell
          align="left"
          style={{ width: 40, ...additionalCellStyle }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <CustomCheckbox
            checked={selected}
            onChange={(event) => {
              onChange?.(event.target.checked);
            }}
          />
        </TableCell>
      )}
      <TableCell align="left" style={{ width: 125, ...additionalCellStyle }}>
        <span>{campaign.uid}</span>
      </TableCell>
      <TableCell align="left" style={{ width: 125, ...additionalCellStyle }}>
        {campaign.brandUid}
      </TableCell>
      <TableCell
        align="left"
        style={{
          width: 175,
          maxWidth: 300,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={campaign.brandCompanyName ?? ''}
      >
        {campaign.brandCompanyName ?? 'N/A'}
      </TableCell>
      <TableCell
        align="left"
        style={{
          width: 175,
          maxWidth: 300,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={campaign.brandName}
      >
        {campaign.brandName}
      </TableCell>
      <TableCell align="left" style={{ width: 125, ...additionalCellStyle }}>
        {campaign.usecase}
      </TableCell>
      <TableCell align="left" style={{ width: 150, ...additionalCellStyle }}>
        {convertTimeWithTimezone(campaign.createDate, undefined, 'MM/DD/YYYY')}
      </TableCell>
      <TableCell
        align="left"
        style={{
          width: 170,
          maxWidth: 300,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={campaign.upstreamCnpName ?? undefined}
      >
        {campaign.upstreamCnpName ?? 'N/A'}
      </TableCell>
      <TableCell
        align="left"
        style={{
          width: 170,
          maxWidth: 300,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={campaign.resellerName ?? undefined}
      >
        {campaign.resellerName ?? 'N/A'}
      </TableCell>
      <TableCell align="left" style={{ width: 125, ...additionalCellStyle }}>
        {campaign.dcaCompletion ? 'Yes' : 'No'}
      </TableCell>
      <TableCell align="left" style={{ width: 92, ...additionalCellStyle }}>
        <Flex
          sx={{
            position: 'relative',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            color: 't.blue100',
            fontWeight: 400,
            fontSize: 'H300',
            lineHeight: 'normal',
            cursor: 'pointer',
            '&:hover': {
              color: '#D41C54',
            },
            ...(expanded ? { top: '-3px', color: '#D41C54' } : {}),
          }}
          onClick={handleExpand}
        >
          <FontAwesomeIcon icon={faEllipsis} />
        </Flex>
        <MnoStatusSection
          ref={mnoStatusSectionRef}
          campaign={campaign}
          mnos={mnos}
          sx={{
            opacity: expanded ? 1 : 0,
            transition: expanded ? 'opacity 0.3s step-end' : 'none',
          }}
        />
      </TableCell>
      <TableCell align="left" style={{ width: 125, ...additionalCellStyle }}>
        {remapTCRStatus(campaign.status)}
      </TableCell>
    </TableRow>
  );
};

export default MyCampaignListingRow;
